(() => {
  const ELEMENT_NAME = 'drbo-shoppable-video';
  const localize = (german, english) => document.documentElement.lang.toLowerCase().split('-')[0] === 'en' ? english : german;

  if (customElements.get(ELEMENT_NAME)) return;

  const SELECTORS = {
    trigger: '[data-drbo-sv-trigger]',
    modal: '[data-drbo-sv-modal]',
    dialog: '[data-drbo-sv-dialog]',
    track: '[data-drbo-sv-track]',
    slide: '[data-drbo-sv-slide]',
    video: '[data-drbo-sv-player] video',
    iframe: 'iframe[data-drbo-sv-iframe]',
    previewVideo: '[data-drbo-sv-preview] video',
    previewIframe: 'iframe[data-drbo-sv-preview-iframe]',
    close: '[data-drbo-sv-close]',
    dismiss: '[data-drbo-sv-dismiss]',
    previous: '[data-drbo-sv-prev]',
    next: '[data-drbo-sv-next]',
    play: '[data-drbo-sv-play]',
    mute: '[data-drbo-sv-mute]',
    previewPlay: '[data-drbo-sv-preview-play]',
    previewMute: '[data-drbo-sv-preview-mute]',
    previewShell: '[data-drbo-sv-preview]',
    productRotator: '[data-drbo-sv-product-rotator]',
    productCard: '[data-drbo-sv-product-card]',
    backdrop: '[data-drbo-sv-backdrop]',
    viewerActions: '.drbo-shoppable-video__viewer-actions',
    counter: '.drbo-shoppable-video__counter',
    counterCurrent: '[data-drbo-sv-counter-current]',
    counterTotal: '[data-drbo-sv-counter-total]',
    progress: '[data-drbo-sv-progress]',
    progressItem: '[data-drbo-sv-progress-item]',
    progressInput: '[data-drbo-sv-progress-input]'
  };

  const TRUE_VALUES = new Set(['', 'true', '1', 'yes', 'on']);
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /*
   * drbo-video-playlist.js contract used by this section:
   * DrboVideoPlaylist.create(video, { sources, loop, onStateChange }) returns
   * { play, pause, seek(seconds), setMuted, getState, destroy }. getState and
   * onStateChange expose aggregate { currentTime, duration, paused, muted }
   * values across every source part. The controller must not fetch a source
   * before create/play/seek is called; this component creates it lazily.
   */

  const bodyLock = {
    owners: new Set(),

    lock(owner) {
      if (this.owners.has(owner)) return;
      this.owners.add(owner);

      if (this.owners.size !== 1) return;
      document.documentElement.classList.add('drbo-sv-scroll-locked');
    },

    unlock(owner) {
      if (!this.owners.delete(owner) || this.owners.size) return;

      document.documentElement.classList.remove('drbo-sv-scroll-locked');
    }
  };

  class DrboShoppableVideo extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.mutationObserver = null;
      this.slides = [];
      this.currentIndex = 0;
      this.isOpen = false;
      this.isPlaying = false;
      this.isMuted = true;
      this.pointerStart = null;
      this.lastFocusedElement = null;
      this.refreshQueued = false;
      this.isPreviewPlaying = true;
      this.isPreviewMuted = true;
      this.inactiveIframeUnloadTimer = null;
      this.previewExpandTimer = null;
      this.previewCollapseTimer = null;
      this.previewReadyTimer = null;
      this.productRotationTimer = null;
      this.progressTimer = null;
      this.metaTimer = null;
      this.mediaCurrentTime = 0;
      this.mediaDuration = 0;
      this.pendingSeekRatio = null;
      this.isProgressInputActive = false;
      this.lastRenderedPlaying = false;
      this.mediaGeneration = 0;
      this.playlistControllers = new Map();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.saveData = Boolean(navigator.connection?.saveData);
    }

    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.isMuted = this.readBoolean('data-drbo-sv-default-muted', true);
      this.toggleAttribute('data-drbo-sv-reduced-motion', this.reducedMotion.matches);
      this.toggleAttribute('data-drbo-sv-save-data', this.saveData);
      this.refresh();
      this.bindEvents();
      this.observeChildren();
      this.applyDismissedState();
      this.startPreview();
    }

    disconnectedCallback() {
      this.close({ restoreFocus: false });
      this.pauseAllMedia();
      this.pausePreview({ preserveState: true });
      this.destroyPlaylistControllers();
      window.clearTimeout(this.inactiveIframeUnloadTimer);
      window.clearTimeout(this.previewExpandTimer);
      window.clearTimeout(this.previewCollapseTimer);
      window.clearTimeout(this.previewReadyTimer);
      window.clearTimeout(this.productRotationTimer);
      window.clearTimeout(this.metaTimer);
      this.stopProgressTracking();
      this.abortController?.abort();
      this.mutationObserver?.disconnect();
      this.abortController = null;
      this.mutationObserver = null;
    }

    get signal() {
      return this.abortController?.signal;
    }

    get modal() {
      return this.querySelector(SELECTORS.modal);
    }

    get dialog() {
      return this.querySelector(SELECTORS.dialog) || this.modal;
    }

    get currentSlide() {
      return this.slides[this.currentIndex] || null;
    }

    get shouldLoop() {
      return this.readBoolean('data-drbo-sv-loop', true);
    }

    get shouldRememberClose() {
      return this.readBoolean('data-drbo-sv-remember-close', true);
    }

    get storageKey() {
      const explicitKey = this.getAttribute('data-drbo-sv-storage-key');
      const sectionId = this.getAttribute('data-section-id') || this.id || 'default';
      return explicitKey || `drbo-shoppable-video:${sectionId}:dismissed`;
    }

    readBoolean(attributeName, fallback) {
      if (!this.hasAttribute(attributeName)) return fallback;
      return TRUE_VALUES.has((this.getAttribute(attributeName) || '').trim().toLowerCase());
    }

    isPlaylistVideo(video) {
      return video instanceof HTMLVideoElement && video.hasAttribute('data-drbo-sv-playlist');
    }

    parsePlaylistSources(video) {
      if (!this.isPlaylistVideo(video)) return [];

      try {
        const sources = JSON.parse(video.getAttribute('data-drbo-video-sources') || '[]');
        return Array.isArray(sources) ? sources : [];
      } catch {
        return [];
      }
    }

    ensurePlaylistController(video) {
      if (!this.isPlaylistVideo(video)) return null;
      if (this.playlistControllers.has(video)) return this.playlistControllers.get(video);

      const sources = this.parsePlaylistSources(video);
      const factory = window.DrboVideoPlaylist;
      if (!sources.length) return null;
      if (typeof factory?.create !== 'function') {
        this.loadPlaylistFallback(video, sources);
        return null;
      }

      let controller = null;
      try {
        controller = factory.create(video, {
          sources,
          loop: this.readVideoLoop(video),
          onStateChange: (state) => this.handlePlaylistState(video, state)
        });
      } catch {
        this.loadPlaylistFallback(video, sources);
        return null;
      }

      if (!controller) return null;
      this.playlistControllers.set(video, controller);
      this.handlePlaylistState(video, this.readPlaylistState(video));
      return controller;
    }

    loadPlaylistFallback(video, sources) {
      if (video.currentSrc || video.getAttribute('src')) return;
      const firstSource = sources[0];
      const sourceUrl = typeof firstSource === 'string' ? firstSource : firstSource?.url || firstSource?.src;
      if (typeof sourceUrl !== 'string' || !sourceUrl) return;
      video.src = sourceUrl;
      video.setAttribute('data-drbo-sv-playlist-fallback', '');
    }

    readVideoLoop(video) {
      if (!video.hasAttribute('data-drbo-video-loop')) return video.loop;
      return TRUE_VALUES.has((video.getAttribute('data-drbo-video-loop') || '').trim().toLowerCase());
    }

    readPlaylistState(video) {
      const controller = this.playlistControllers.get(video);
      if (controller && typeof controller.getState === 'function') {
        try {
          return controller.getState() || {};
        } catch {
          return {};
        }
      }

      return {
        currentTime: video.currentTime,
        duration: video.duration,
        paused: video.paused,
        muted: video.muted || video.volume === 0
      };
    }

    handlePlaylistState(video, state = {}) {
      if (!(video instanceof HTMLVideoElement) || !video.isConnected) return;

      const isPreview = video.matches(SELECTORS.previewVideo);
      const slide = video.closest(SELECTORS.slide);
      const currentTime = Number(state.currentTime);
      const duration = Number(state.duration);
      const paused = typeof state.paused === 'boolean' ? state.paused : video.paused;
      const muted = typeof state.muted === 'boolean' ? state.muted : video.muted || video.volume === 0;

      if (isPreview) {
        this.isPreviewPlaying = !paused;
        this.toggleAttribute('data-drbo-sv-preview-playing', this.isPreviewPlaying);
        if (!paused) this.markPreviewMediaReady(120);
        this.renderPreviewState();
        return;
      }

      if (!slide || slide !== this.currentSlide) return;
      if (!paused) slide.setAttribute('data-video-ready', 'true');
      this.updateMediaProgress(currentTime, duration);
      this.isPlaying = !paused;
      if (!paused) this.isMuted = muted;
      this.renderMediaState();
    }

    setPlaylistMuted(video, muted) {
      const controller = this.playlistControllers.get(video);
      if (controller && typeof controller.setMuted === 'function') {
        controller.setMuted(muted);
        return;
      }
      video.muted = muted;
    }

    playVideo(video) {
      const controller = this.ensurePlaylistController(video);
      this.setPlaylistMuted(video, video.matches(SELECTORS.previewVideo) ? true : this.isMuted);
      if (controller && typeof controller.play === 'function') return controller.play();
      return video.play();
    }

    pauseVideo(video, { mute = true } = {}) {
      const controller = this.playlistControllers.get(video);
      if (controller && typeof controller.pause === 'function') controller.pause();
      else video.pause();
      if (mute) this.setPlaylistMuted(video, true);
    }

    destroyPlaylistControllers() {
      this.playlistControllers.forEach((controller) => {
        if (typeof controller.destroy === 'function') controller.destroy();
      });
      this.playlistControllers.clear();
    }

    releaseDetachedPlaylistControllers() {
      this.playlistControllers.forEach((controller, video) => {
        if (this.contains(video)) return;
        if (typeof controller.destroy === 'function') controller.destroy();
        this.playlistControllers.delete(video);
      });
    }

    refresh() {
      this.releaseDetachedPlaylistControllers();
      const previousSlide = this.currentSlide;
      const previousBlockId = previousSlide?.getAttribute('data-block-id') || previousSlide?.getAttribute('data-drbo-sv-block-id');
      this.slides = Array.from(this.querySelectorAll(SELECTORS.slide));

      if (previousBlockId) {
        const matchedIndex = this.slides.findIndex((slide) => {
          return slide.getAttribute('data-block-id') === previousBlockId || slide.getAttribute('data-drbo-sv-block-id') === previousBlockId;
        });
        this.currentIndex = matchedIndex >= 0 ? matchedIndex : Math.min(this.currentIndex, Math.max(this.slides.length - 1, 0));
      } else {
        const presetIndex = this.slides.findIndex((slide) => slide.hasAttribute('data-drbo-sv-active'));
        this.currentIndex = presetIndex >= 0 ? presetIndex : Math.min(this.currentIndex, Math.max(this.slides.length - 1, 0));
      }

      this.slides.forEach((slide, index) => this.prepareSlide(slide, index));
      this.render();
    }

    prepareSlide(slide, index) {
      slide.setAttribute('data-drbo-sv-index', String(index));

      if (!slide.id) {
        const rootId = this.id || 'drbo-shoppable-video';
        slide.id = `${rootId}-slide-${index + 1}`;
      }

      slide.querySelectorAll(SELECTORS.video).forEach((video) => {
        video.playsInline = true;
        video.muted = this.isMuted;
        video.setAttribute('playsinline', '');
      });

      slide.querySelectorAll(SELECTORS.productRotator).forEach((rotator) => {
        this.renderProductCards(rotator, 0);
      });

      slide.querySelectorAll(SELECTORS.iframe).forEach((iframe) => {
        iframe.setAttribute('tabindex', '-1');
        iframe.setAttribute('allow', this.mergeAllowPermissions(iframe.getAttribute('allow')));
        if (iframe.hasAttribute('data-drbo-sv-load-bound')) return;
        iframe.setAttribute('data-drbo-sv-load-bound', '');
        iframe.addEventListener('load', () => {
          iframe.setAttribute('data-drbo-sv-ready', '');
          slide.setAttribute('data-video-ready', 'true');
          if (!this.isOpen || slide !== this.currentSlide) return;
          if (Number(iframe.dataset.mediaGeneration) !== this.mediaGeneration) return;
          this.registerIframeEvents(iframe);
          this.sendIframeCommand(iframe, this.isMuted ? 'mute' : 'unmute');
          const shouldPlay = iframe.dataset.drboSvShouldPlay === 'true';
          this.sendIframeCommand(iframe, shouldPlay ? 'play' : 'pause');
        }, { signal: this.signal });
      });
    }

    mergeAllowPermissions(value) {
      const permissions = new Set((value || '').split(';').map((item) => item.trim()).filter(Boolean));
      ['autoplay', 'fullscreen', 'picture-in-picture'].forEach((permission) => permissions.add(permission));
      return Array.from(permissions).join('; ');
    }

    bindEvents() {
      const listenerOptions = { signal: this.signal };

      this.addEventListener('click', (event) => this.handleClick(event), listenerOptions);
      this.addEventListener('play', (event) => this.handleNativeMediaState(event), { ...listenerOptions, capture: true });
      this.addEventListener('pause', (event) => this.handleNativeMediaState(event), { ...listenerOptions, capture: true });
      this.addEventListener('volumechange', (event) => this.handleNativeMediaState(event), { ...listenerOptions, capture: true });
      this.addEventListener('timeupdate', (event) => this.handleNativeMediaState(event), { ...listenerOptions, capture: true });
      this.addEventListener('durationchange', (event) => this.handleNativeMediaState(event), { ...listenerOptions, capture: true });
      this.addEventListener('loadedmetadata', (event) => this.handleNativeMediaState(event), { ...listenerOptions, capture: true });
      this.addEventListener('ended', (event) => this.handleNativeMediaState(event), { ...listenerOptions, capture: true });
      this.addEventListener('input', (event) => this.handleProgressInput(event), listenerOptions);
      this.addEventListener('change', (event) => this.handleProgressInput(event, { commit: true }), listenerOptions);
      document.addEventListener('keydown', (event) => this.handleKeydown(event), listenerOptions);
      document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), listenerOptions);
      document.addEventListener('visibilitychange', () => this.handleVisibilityChange(), listenerOptions);
      window.addEventListener('message', (event) => this.handlePlayerMessage(event), listenerOptions);
      this.modal?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      }, listenerOptions);
      this.modal?.addEventListener('close', () => {
        if (this.isOpen) this.close();
      }, listenerOptions);

      this.addEventListener('pointerdown', (event) => this.handlePointerDown(event), listenerOptions);
      this.addEventListener('pointerdown', (event) => this.handleViewerInteraction(event), listenerOptions);
      this.addEventListener('pointerup', (event) => this.handlePointerUp(event), listenerOptions);
      this.addEventListener('pointerdown', (event) => {
        if (event.target instanceof Element && event.target.closest(SELECTORS.progressInput)) this.isProgressInputActive = true;
      }, listenerOptions);
      this.addEventListener('pointerup', (event) => {
        if (!(event.target instanceof Element) || !event.target.closest(SELECTORS.progressInput)) return;
        this.isProgressInputActive = false;
        this.renderProgress();
      }, listenerOptions);
      window.addEventListener('pointerup', () => this.finishProgressInteraction(), listenerOptions);
      window.addEventListener('pointercancel', () => this.finishProgressInteraction(), listenerOptions);
      this.addEventListener('pointermove', (event) => this.handleViewerInteraction(event), listenerOptions);
      this.addEventListener('pointerover', (event) => this.handleProductInteractionStart(event), listenerOptions);
      this.addEventListener('pointerout', (event) => this.handleProductInteractionEnd(event), listenerOptions);
      this.addEventListener('focusin', (event) => {
        this.handleProductInteractionStart(event);
        this.handleViewerInteraction(event);
      }, listenerOptions);
      this.addEventListener('focusout', (event) => this.handleProductInteractionEnd(event), listenerOptions);
      this.addEventListener('pointercancel', () => {
        this.pointerStart = null;
      }, listenerOptions);
    }

    observeChildren() {
      this.mutationObserver = new MutationObserver((records) => {
        const slidesChanged = records.some((record) => {
          return [...record.addedNodes, ...record.removedNodes].some((node) => {
            return node instanceof Element && (node.matches(SELECTORS.slide) || node.querySelector(SELECTORS.slide));
          });
        });

        if (!slidesChanged) return;
        if (this.refreshQueued) return;

        this.refreshQueued = true;
        queueMicrotask(() => {
          this.refreshQueued = false;
          if (this.isConnected) this.refresh();
        });
      });

      this.mutationObserver.observe(this, { childList: true, subtree: true });
    }

    handleClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest(SELECTORS.dismiss)) {
        event.preventDefault();
        this.dismiss();
        return;
      }

      if (target.closest(SELECTORS.previewPlay)) {
        event.preventDefault();
        this.togglePreviewPlayback();
        return;
      }

      if (target.closest(SELECTORS.previewMute)) {
        event.preventDefault();
        this.togglePreviewMute();
        return;
      }

      if (target.closest(SELECTORS.trigger)) {
        event.preventDefault();
        this.open();
        return;
      }

      if (target.closest(SELECTORS.close)) {
        event.preventDefault();
        this.close();
        return;
      }

      if (target.closest(SELECTORS.previous)) {
        event.preventDefault();
        this.previous();
        return;
      }

      if (target.closest(SELECTORS.next)) {
        event.preventDefault();
        this.next();
        return;
      }

      if (target.closest(SELECTORS.play)) {
        event.preventDefault();
        this.togglePlayback();
        return;
      }

      if (target.closest(SELECTORS.mute)) {
        event.preventDefault();
        this.toggleMute();
        return;
      }

      const selectedSlide = target.closest(SELECTORS.slide);
      if (selectedSlide && selectedSlide !== this.currentSlide) {
        event.preventDefault();
        this.goTo(this.slides.indexOf(selectedSlide), { autoplay: this.isOpen });
        return;
      }

      const backdrop = target.closest(SELECTORS.backdrop);
      if (backdrop && target === backdrop) this.close();
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'ArrowLeft' && !this.isInteractiveTarget(event.target)) {
        event.preventDefault();
        this.previous();
        return;
      }

      if (event.key === 'ArrowRight' && !this.isInteractiveTarget(event.target)) {
        event.preventDefault();
        this.next();
        return;
      }

      if (event.key === ' ' && !this.isInteractiveTarget(event.target)) {
        event.preventDefault();
        this.togglePlayback();
        return;
      }

      if (event.key === 'Tab') this.keepFocusInside(event);
    }

    isInteractiveTarget(target) {
      return target instanceof Element && Boolean(target.closest('a, button, input, select, textarea, [contenteditable="true"]'));
    }

    finishProgressInteraction() {
      if (!this.isProgressInputActive) return;
      this.isProgressInputActive = false;
      this.renderProgress();
    }

    handleViewerInteraction(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!this.isOpen || !target?.closest(SELECTORS.dialog)) return;
      this.showMetadata();
      if (this.isPlaying) this.scheduleMetadataHide();
    }

    keepFocusInside(event) {
      const dialog = this.dialog;
      if (!dialog) return;

      const focusableElements = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
        return !element.hasAttribute('hidden') && !element.closest('[inert], [aria-hidden="true"]');
      });

      if (!focusableElements.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    handlePointerDown(event) {
      if (!this.isOpen || event.pointerType === 'mouse') return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('[data-drbo-sv-stage]')) return;
      if (target.closest('a, button, [data-drbo-sv-product]')) return;
      this.pointerStart = { x: event.clientX, y: event.clientY, time: performance.now() };
    }

    handlePointerUp(event) {
      if (!this.pointerStart || !this.isOpen) return;

      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      const elapsed = Math.max(performance.now() - this.pointerStart.time, 1);
      const velocity = Math.abs(deltaX) / elapsed;
      this.pointerStart = null;

      if (Math.abs(deltaX) < 48 && velocity < 0.35) return;
      if (Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;

      if (deltaX < 0) this.next();
      else this.previous();
    }

    handleProductInteractionStart(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest(SELECTORS.productRotator)) return;
      window.clearTimeout(this.productRotationTimer);
    }

    handleProductInteractionEnd(event) {
      const target = event.target instanceof Element ? event.target : null;
      const rotator = target?.closest(SELECTORS.productRotator);
      if (!rotator) return;
      if (event.relatedTarget instanceof Node && rotator.contains(event.relatedTarget)) return;
      this.scheduleProductRotation();
    }

    handleNativeMediaState(event) {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement)) return;
      if (!video.closest(SELECTORS.slide) || video.closest(SELECTORS.slide) !== this.currentSlide) return;

      if (this.isPlaylistVideo(video)) {
        this.handlePlaylistState(video, this.readPlaylistState(video));
        return;
      }

      this.updateMediaProgress(video.currentTime, video.duration);
      this.isPlaying = !video.paused;
      this.isMuted = video.muted || video.volume === 0;
      this.renderMediaState();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        window.clearTimeout(this.productRotationTimer);
        this.stopProgressTracking();
        if (this.isOpen) this.pauseCurrent();
        else this.pausePreview({ preserveState: true });
        return;
      }

      if (this.isOpen) {
        this.scheduleProductRotation();
        this.startProgressTracking();
      }
      else this.startPreview();
    }

    handlePlayerMessage(event) {
      const iframe = this.currentSlide?.querySelector(SELECTORS.iframe);
      if (!iframe || event.source !== iframe.contentWindow) return;

      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }

      if (!data || typeof data !== 'object') return;

      if (data.event === 'onReady') {
        this.registerIframeEvents(iframe);
        this.sendIframeCommand(iframe, this.isMuted ? 'mute' : 'unmute');
        if (this.isOpen && iframe.dataset.drboSvShouldPlay === 'true') this.sendIframeCommand(iframe, 'play');
        return;
      }

      let playerState = null;
      let progressChanged = false;

      if (data.event === 'infoDelivery' || data.event === 'initialDelivery') {
        const currentTime = Number(data.info?.currentTime);
        const duration = Number(data.info?.duration);
        if (Number.isFinite(currentTime) || Number.isFinite(duration)) {
          this.updateMediaProgress(currentTime, duration);
          progressChanged = true;
        }
      }

      if (data.event === 'timeupdate') {
        const currentTime = Number(data.data?.seconds);
        const duration = Number(data.data?.duration);
        if (Number.isFinite(currentTime) || Number.isFinite(duration)) {
          this.updateMediaProgress(currentTime, duration);
          progressChanged = true;
        }
      }

      if (data.method === 'getCurrentTime' && Number.isFinite(Number(data.value))) {
        this.updateMediaProgress(Number(data.value), this.mediaDuration);
        progressChanged = true;
      }

      if (data.method === 'getDuration' && Number.isFinite(Number(data.value))) {
        this.updateMediaProgress(this.mediaCurrentTime, Number(data.value));
        progressChanged = true;
      }

      if (data.event === 'onStateChange') playerState = data.info;
      if (data.event === 'infoDelivery' && Number.isFinite(data.info?.playerState)) playerState = data.info.playerState;

      if (playerState !== null) {
        this.isPlaying = Number(playerState) === 1;
        this.renderMediaState();
        return;
      }

      if (data.event === 'play' || data.event === 'playing') this.isPlaying = true;
      else if (data.event === 'pause' || data.event === 'ended') this.isPlaying = false;
      else if (!progressChanged) return;

      this.renderMediaState();
    }

    registerIframeEvents(iframe) {
      const provider = this.getIframeProvider(iframe);
      if (provider === 'youtube') {
        this.registerYoutubeIframe(iframe);
      }

      if (provider === 'vimeo') {
        ['play', 'pause', 'ended', 'timeupdate'].forEach((eventName) => {
          try {
            iframe.contentWindow?.postMessage({ method: 'addEventListener', value: eventName }, new URL(iframe.src).origin);
          } catch {
            return;
          }
        });
      }
    }

    registerYoutubeIframe(iframe) {
      if (!iframe.contentWindow || !iframe.getAttribute('src')) return;
      this.postIframeMessage(iframe, JSON.stringify({ event: 'listening', id: iframe.id || '' }));
      this.postIframeMessage(iframe, JSON.stringify({ event: 'command', func: 'addEventListener', args: ['onStateChange'] }));
    }

    handleBlockSelect(event) {
      const eventTarget = event.target instanceof Element ? event.target : null;
      const detailBlockId = event.detail?.blockId;
      let slide = eventTarget?.closest(SELECTORS.slide) || null;

      if (!slide && detailBlockId) {
        slide = this.slides.find((candidate) => {
          return candidate.getAttribute('data-block-id') === detailBlockId || candidate.getAttribute('data-drbo-sv-block-id') === detailBlockId;
        }) || null;
      }

      if (!slide || !this.contains(slide)) return;

      if (!this.slides.includes(slide)) this.refresh();

      this.removeDismissedState();
      this.open({ focus: false, autoplay: false });
      this.goTo(this.slides.indexOf(slide), { autoplay: false });
    }

    open({ focus = true, autoplay = true } = {}) {
      if (this.isOpen || !this.modal || !this.slides.length) return;

      this.removeDismissedState();
      this.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.isOpen = true;
      this.setAttribute('data-drbo-sv-open', '');
      this.modal.removeAttribute('hidden');
      this.modal.setAttribute('aria-hidden', 'false');

      if (this.modal instanceof HTMLDialogElement && !this.modal.open) {
        try {
          this.modal.showModal();
        } catch {
          this.modal.setAttribute('open', '');
        }
      }

      bodyLock.lock(this);
      this.pausePreview({ preserveState: true });
      this.goTo(this.currentIndex, { autoplay, force: true });
      this.startProgressTracking();

      if (focus) {
        const focusTarget = this.modal.querySelector(SELECTORS.close) || this.dialog;
        if (focusTarget instanceof HTMLElement) {
          if (!focusTarget.matches(FOCUSABLE_SELECTOR)) focusTarget.setAttribute('tabindex', '-1');
          focusTarget.focus({ preventScroll: true });
        }
      }

      this.dispatchEvent(new CustomEvent('drbo-sv:open', { bubbles: true, detail: { index: this.currentIndex } }));
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;

      window.clearTimeout(this.inactiveIframeUnloadTimer);
      window.clearTimeout(this.productRotationTimer);
      window.clearTimeout(this.metaTimer);
      this.stopProgressTracking();
      this.mediaGeneration += 1;
      this.pauseAllMedia();
      this.unloadInactiveIframes({ includeCurrent: true });
      this.isOpen = false;
      this.removeAttribute('data-drbo-sv-open');

      const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const focusReturnTarget = restoreFocus && this.lastFocusedElement?.isConnected ? this.lastFocusedElement : null;

      if (activeElement && this.modal?.contains(activeElement)) {
        if (focusReturnTarget && !this.modal.contains(focusReturnTarget)) {
          focusReturnTarget.focus({ preventScroll: true });
        } else {
          activeElement.blur();
        }
      }

      this.modal?.setAttribute('aria-hidden', 'true');

      if (this.modal instanceof HTMLDialogElement && this.modal.open) {
        this.modal.close();
      }

      this.modal?.setAttribute('hidden', '');
      bodyLock.unlock(this);
      this.isMuted = this.readBoolean('data-drbo-sv-default-muted', true);
      this.renderMediaState();
      this.startPreview({ force: this.readBoolean('data-drbo-sv-preview-reopen-after-close', true) });

      if (focusReturnTarget && document.activeElement !== focusReturnTarget) {
        focusReturnTarget.focus({ preventScroll: true });
      }

      this.dispatchEvent(new CustomEvent('drbo-sv:close', { bubbles: true, detail: { index: this.currentIndex } }));
    }

    dismiss() {
      this.close({ restoreFocus: false });
      this.pausePreview();
      this.setAttribute('data-drbo-sv-dismissed', '');
      this.setDismissedState();
      this.dispatchEvent(new CustomEvent('drbo-sv:dismiss', { bubbles: true }));
    }

    previous() {
      this.goTo(this.currentIndex - 1, { autoplay: this.isOpen });
    }

    next() {
      this.goTo(this.currentIndex + 1, { autoplay: this.isOpen });
    }

    goTo(index, { autoplay = false, force = false, seekRatio = null } = {}) {
      if (!this.slides.length) return;

      let nextIndex = Number(index);
      if (!Number.isFinite(nextIndex)) return;

      if (this.shouldLoop) {
        nextIndex = ((nextIndex % this.slides.length) + this.slides.length) % this.slides.length;
      } else {
        nextIndex = Math.min(Math.max(nextIndex, 0), this.slides.length - 1);
      }

      if (!force && nextIndex === this.currentIndex) {
        if (Number.isFinite(Number(seekRatio))) this.seekToRatio(Number(seekRatio));
        this.resetProductRotation();
        if (autoplay) this.playCurrent();
        return;
      }

      this.pauseCurrent();
      this.mediaGeneration += 1;
      this.mediaCurrentTime = 0;
      this.mediaDuration = 0;
      this.pendingSeekRatio = Number.isFinite(Number(seekRatio)) ? Math.min(Math.max(Number(seekRatio), 0), 1) : null;
      this.currentIndex = nextIndex;
      this.loadCurrentIframe(autoplay);
      this.render();
      this.showMetadata();
      this.resetProductRotation();
      this.scheduleInactiveIframeUnload();

      if (autoplay) this.playCurrent();
      if (this.isOpen) this.startProgressTracking();

      this.dispatchEvent(new CustomEvent('drbo-sv:change', {
        bubbles: true,
        detail: { index: this.currentIndex, slide: this.currentSlide }
      }));
    }

    render() {
      const total = this.slides.length;
      this.setAttribute('data-drbo-sv-current-index', String(this.currentIndex));
      this.setAttribute('data-drbo-sv-count', String(total));

      this.slides.forEach((slide, index) => {
        const isActive = index === this.currentIndex;
        const previousIndex = (this.currentIndex - 1 + total) % total;
        const nextIndex = (this.currentIndex + 1) % total;
        let position = 'hidden';
        if (isActive) position = 'active';
        else if (total > 1 && index === previousIndex) position = 'previous';
        else if (total > 1 && index === nextIndex) position = 'next';
        slide.toggleAttribute('data-drbo-sv-active', isActive);
        slide.setAttribute('data-position', position);
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.toggleAttribute('inert', position === 'hidden');
      });

      this.querySelectorAll(SELECTORS.counterCurrent).forEach((element) => {
        element.textContent = total ? String(this.currentIndex + 1) : '0';
      });

      this.querySelectorAll(SELECTORS.counterTotal).forEach((element) => {
        element.textContent = String(total);
      });

      const previousDisabled = !this.shouldLoop && this.currentIndex <= 0;
      const nextDisabled = !this.shouldLoop && this.currentIndex >= total - 1;
      this.setControlDisabled(SELECTORS.previous, previousDisabled || total < 2);
      this.setControlDisabled(SELECTORS.next, nextDisabled || total < 2);
      this.mountViewerChromeToCurrentSlide();
      this.renderMediaState();
      this.renderProgress();
    }

    mountViewerChromeToCurrentSlide() {
      const videoWrap = this.currentSlide?.querySelector('[data-drbo-sv-player]');
      if (!videoWrap) return;

      [SELECTORS.viewerActions, SELECTORS.counter, SELECTORS.progress].forEach((selector) => {
        const element = this.querySelector(selector);
        if (element && element.parentElement !== videoWrap) videoWrap.append(element);
      });
    }

    handleProgressInput(event, { commit = false } = {}) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches(SELECTORS.progressInput) || !this.slides.length) return;

      const localRatio = Math.min(Math.max(Number(input.value) / Number(input.max || 1000), 0), 1);

      this.isProgressInputActive = !commit;
      this.seekToRatio(localRatio);
      if (commit) {
        this.isProgressInputActive = false;
        this.renderProgress();
      }
    }

    updateMediaProgress(currentTime, duration) {
      if (Number.isFinite(currentTime) && currentTime >= 0) this.mediaCurrentTime = currentTime;
      if (Number.isFinite(duration) && duration > 0) this.mediaDuration = duration;
      this.applyPendingSeek();
      this.renderProgress();
    }

    applyPendingSeek() {
      if (!Number.isFinite(this.pendingSeekRatio) || !(this.mediaDuration > 0)) return;
      const ratio = this.pendingSeekRatio;
      this.pendingSeekRatio = null;
      this.seekToRatio(ratio);
    }

    seekToRatio(ratio) {
      const safeRatio = Math.min(Math.max(Number(ratio) || 0, 0), 1);
      if (!(this.mediaDuration > 0)) {
        this.pendingSeekRatio = safeRatio;
        return;
      }

      const targetTime = Math.min(safeRatio * this.mediaDuration, Math.max(this.mediaDuration - 0.05, 0));
      const video = this.currentSlide?.querySelector(SELECTORS.video);
      const iframe = this.currentSlide?.querySelector(SELECTORS.iframe);

      if (video instanceof HTMLVideoElement && this.isPlaylistVideo(video)) {
        const controller = this.ensurePlaylistController(video);
        if (controller && typeof controller.seek === 'function') controller.seek(targetTime);
        else video.currentTime = targetTime;
      } else if (video instanceof HTMLVideoElement) {
        video.currentTime = targetTime;
      }
      if (iframe) this.sendIframeSeek(iframe, targetTime);

      this.mediaCurrentTime = targetTime;
      this.renderProgress();
    }

    renderProgress() {
      const localRatio = this.mediaDuration > 0 ? Math.min(Math.max(this.mediaCurrentTime / this.mediaDuration, 0), 1) : 0;

      this.querySelectorAll(SELECTORS.progressItem).forEach((item) => {
        item.style.setProperty('--drbo-sv-progress', String(localRatio));
      });

      if (this.isProgressInputActive) return;
      this.querySelectorAll(SELECTORS.progressInput).forEach((input) => {
        input.value = String(Math.round(localRatio * Number(input.max || 1000)));
        input.setAttribute('aria-valuetext', `Video ${this.currentIndex + 1} von ${this.slides.length}`);
      });
    }

    startProgressTracking() {
      this.stopProgressTracking();
      if (!this.isOpen) return;

      const update = () => {
        if (!this.isOpen) return;
        const video = this.currentSlide?.querySelector(SELECTORS.video);
        const iframe = this.currentSlide?.querySelector(SELECTORS.iframe);
        if (video instanceof HTMLVideoElement && this.isPlaylistVideo(video)) {
          this.handlePlaylistState(video, this.readPlaylistState(video));
        } else if (video instanceof HTMLVideoElement) {
          this.updateMediaProgress(video.currentTime, video.duration);
        }
        if (iframe) this.requestIframeProgress(iframe);
      };

      update();
      this.progressTimer = window.setInterval(update, 250);
    }

    stopProgressTracking() {
      window.clearInterval(this.progressTimer);
      this.progressTimer = null;
    }

    requestIframeProgress(iframe) {
      const provider = this.getIframeProvider(iframe);
      if (!iframe.contentWindow || !iframe.getAttribute('src') || !iframe.hasAttribute('data-drbo-sv-ready')) return;

      if (provider === 'youtube') {
        const pollCount = (Number(iframe.dataset.drboSvProgressPoll) || 0) + 1;
        iframe.dataset.drboSvProgressPoll = String(pollCount);
        if (!(this.mediaDuration > 0) && pollCount % 8 === 1) this.registerYoutubeIframe(iframe);
        this.postIframeMessage(iframe, JSON.stringify({ event: 'command', func: 'getCurrentTime', args: [] }));
        this.postIframeMessage(iframe, JSON.stringify({ event: 'command', func: 'getDuration', args: [] }));
      }

      if (provider === 'vimeo') {
        this.postIframeMessage(iframe, { method: 'getCurrentTime' });
        this.postIframeMessage(iframe, { method: 'getDuration' });
      }
    }

    sendIframeSeek(iframe, targetTime) {
      if (!iframe.hasAttribute('data-drbo-sv-ready')) return;
      const provider = this.getIframeProvider(iframe);
      if (provider === 'youtube') this.postIframeMessage(iframe, JSON.stringify({ event: 'command', func: 'seekTo', args: [targetTime, true] }));
      if (provider === 'vimeo') this.postIframeMessage(iframe, { method: 'setCurrentTime', value: targetTime });
    }

    postIframeMessage(iframe, message) {
      const source = iframe.getAttribute('src');
      if (!iframe.contentWindow || !source) return;
      try {
        iframe.contentWindow.postMessage(message, new URL(source).origin);
      } catch {
        iframe.contentWindow.postMessage(message, '*');
      }
    }

    get currentProductRotator() {
      return this.currentSlide?.querySelector(SELECTORS.productRotator) || null;
    }

    renderProductCards(rotator, index) {
      const cards = Array.from(rotator.querySelectorAll(SELECTORS.productCard));
      if (!cards.length) return;
      const nextIndex = ((Number(index) || 0) % cards.length + cards.length) % cards.length;
      rotator.dataset.productIndex = String(nextIndex);
      cards.forEach((card, cardIndex) => {
        const active = cardIndex === nextIndex;
        card.toggleAttribute('hidden', !active);
        card.toggleAttribute('inert', !active);
      });
    }

    resetProductRotation() {
      window.clearTimeout(this.productRotationTimer);
      const rotator = this.currentProductRotator;
      if (!rotator) return;
      this.renderProductCards(rotator, 0);
      this.scheduleProductRotation();
    }

    scheduleProductRotation() {
      window.clearTimeout(this.productRotationTimer);
      if (!this.isOpen || document.hidden || this.reducedMotion.matches) return;
      if (!this.readBoolean('data-drbo-sv-product-rotation', true)) return;
      const rotator = this.currentProductRotator;
      const cards = rotator ? Array.from(rotator.querySelectorAll(SELECTORS.productCard)) : [];
      if (!rotator || cards.length < 2) return;
      const interval = Math.max(Number(this.getAttribute('data-drbo-sv-product-rotation-interval')) || 7000, 5000);
      this.productRotationTimer = window.setTimeout(() => {
        if (!this.isOpen || document.hidden || rotator !== this.currentProductRotator) return;
        const nextIndex = (Number(rotator.dataset.productIndex) + 1) % cards.length;
        this.renderProductCards(rotator, nextIndex);
        this.scheduleProductRotation();
      }, interval);
    }

    setControlDisabled(selector, disabled) {
      this.querySelectorAll(selector).forEach((control) => {
        if (control instanceof HTMLButtonElement) control.disabled = disabled;
        control.setAttribute('aria-disabled', String(disabled));
      });
    }

    renderMediaState() {
      if (this.lastRenderedPlaying !== this.isPlaying) {
        this.lastRenderedPlaying = this.isPlaying;
        if (this.isPlaying) this.scheduleMetadataHide();
        else this.showMetadata();
      }

      this.toggleAttribute('data-drbo-sv-playing', this.isPlaying);
      this.toggleAttribute('data-drbo-sv-muted', this.isMuted);

      this.querySelectorAll(SELECTORS.play).forEach((control) => {
        control.setAttribute('aria-pressed', String(this.isPlaying));
        control.setAttribute('aria-label', this.isPlaying ? localize("Video pausieren", "Pause video") : localize("Video abspielen", "Play video"));
        control.setAttribute('data-drbo-sv-state', this.isPlaying ? 'playing' : 'paused');
      });

      this.querySelectorAll(SELECTORS.mute).forEach((control) => {
        control.setAttribute('aria-pressed', String(!this.isMuted));
        control.setAttribute('aria-label', this.isMuted ? localize("Ton einschalten", "Unmute") : localize("Ton ausschalten", "Mute"));
        control.setAttribute('data-drbo-sv-state', this.isMuted ? 'muted' : 'unmuted');
      });
    }

    showMetadata() {
      window.clearTimeout(this.metaTimer);
      this.removeAttribute('data-drbo-sv-meta-hidden');
    }

    scheduleMetadataHide() {
      window.clearTimeout(this.metaTimer);
      this.removeAttribute('data-drbo-sv-meta-hidden');
      if (!this.isOpen || !this.isPlaying) return;

      const duration = Math.max(Number(this.getAttribute('data-drbo-sv-meta-duration')) || 4000, 2000);
      this.metaTimer = window.setTimeout(() => {
        if (!this.isOpen || !this.isPlaying) return;
        this.setAttribute('data-drbo-sv-meta-hidden', '');
      }, duration);
    }

    togglePlayback() {
      if (this.isPlaying) this.pauseCurrent();
      else this.playCurrent();
    }

    toggleMute() {
      this.isMuted = !this.isMuted;

      this.currentSlide?.querySelectorAll(SELECTORS.video).forEach((video) => {
        this.setPlaylistMuted(video, this.isMuted);
      });

      this.currentSlide?.querySelectorAll(SELECTORS.iframe).forEach((iframe) => {
        this.sendIframeCommand(iframe, this.isMuted ? 'mute' : 'unmute');
      });

      this.renderMediaState();
    }

    async playCurrent() {
      const slide = this.currentSlide;
      if (!slide) return;

      this.pauseAllMedia(slide);
      this.loadCurrentIframe(true);
      const videos = Array.from(slide.querySelectorAll(SELECTORS.video));
      const iframes = Array.from(slide.querySelectorAll(SELECTORS.iframe));

      if (videos.length) {
        await Promise.allSettled(videos.map((video) => this.playVideo(video)));
        this.isPlaying = videos.some((video) => {
          if (!this.isPlaylistVideo(video)) return !video.paused;
          return this.readPlaylistState(video).paused === false;
        });
      } else if (iframes.length) {
        iframes.forEach((iframe) => {
          iframe.dataset.drboSvShouldPlay = 'true';
          this.sendIframeCommand(iframe, 'play');
        });
      } else {
        this.isPlaying = false;
      }

      this.renderMediaState();
    }

    pauseCurrent() {
      const slide = this.currentSlide;
      if (!slide) return;

      slide.querySelectorAll(SELECTORS.video).forEach((video) => {
        this.pauseVideo(video);
      });
      slide.querySelectorAll(SELECTORS.iframe).forEach((iframe) => {
        iframe.dataset.drboSvShouldPlay = 'false';
        this.sendIframeCommand(iframe, 'pause');
        this.sendIframeCommand(iframe, 'mute');
      });
      this.isPlaying = false;
      this.renderMediaState();
    }

    unloadInactiveIframes({ includeCurrent = false } = {}) {
      this.slides.forEach((slide) => {
        if (!includeCurrent && slide === this.currentSlide) return;
        slide.removeAttribute('data-video-ready');
        slide.querySelectorAll(SELECTORS.iframe).forEach((iframe) => {
          if (!iframe.getAttribute('src')) return;
          iframe.removeAttribute('data-drbo-sv-ready');
          iframe.removeAttribute('src');
        });
      });
    }

    scheduleInactiveIframeUnload() {
      window.clearTimeout(this.inactiveIframeUnloadTimer);
      this.inactiveIframeUnloadTimer = window.setTimeout(() => {
        this.unloadInactiveIframes();
      }, 420);
    }

    pauseAllMedia(exceptSlide = null) {
      this.slides.forEach((slide) => {
        if (slide === exceptSlide) return;
        slide.querySelectorAll(SELECTORS.video).forEach((video) => {
          this.pauseVideo(video);
        });
        slide.querySelectorAll(SELECTORS.iframe).forEach((iframe) => {
          iframe.dataset.drboSvShouldPlay = 'false';
          this.sendIframeCommand(iframe, 'pause');
          this.sendIframeCommand(iframe, 'mute');
        });
      });

      if (!exceptSlide) {
        this.isPlaying = false;
        this.renderMediaState();
      }
    }

    loadCurrentIframe(autoplay = false) {
      this.currentSlide?.querySelectorAll(SELECTORS.iframe).forEach((iframe) => {
        iframe.dataset.drboSvShouldPlay = String(autoplay);
        if (iframe.getAttribute('src') || !iframe.dataset.src) return;
        iframe.removeAttribute('data-drbo-sv-ready');
        iframe.dataset.mediaGeneration = String(this.mediaGeneration);
        this.currentSlide?.removeAttribute('data-video-ready');
        iframe.src = this.buildIframeSource(iframe.dataset.src, this.getIframeProvider(iframe), autoplay);
      });
    }

    buildIframeSource(source, provider, autoplay) {
      try {
        const url = new URL(source, window.location.href);
        url.searchParams.set('autoplay', autoplay ? '1' : '0');

        if (provider === 'youtube') {
          url.searchParams.set('enablejsapi', '1');
          url.searchParams.set('playsinline', '1');
          url.searchParams.set('origin', window.location.origin);
          url.searchParams.set('mute', '1');
        }

        if (provider === 'vimeo') {
          url.searchParams.set('api', '1');
          url.searchParams.set('playsinline', '1');
          url.searchParams.set('muted', '1');
        }

        return url.toString();
      } catch {
        return source;
      }
    }

    getIframeProvider(iframe) {
      const explicitProvider = iframe.getAttribute('data-drbo-sv-provider');
      if (explicitProvider) return explicitProvider.toLowerCase();

      const source = iframe.dataset.src || iframe.src || '';
      if (/youtu\.be|youtube(?:-nocookie)?\.com/i.test(source)) return 'youtube';
      if (/vimeo\.com/i.test(source)) return 'vimeo';
      return 'unknown';
    }

    sendIframeCommand(iframe, command) {
      const loadedSource = iframe.getAttribute('src');
      if (!iframe.contentWindow || !loadedSource || !iframe.hasAttribute('data-drbo-sv-ready')) return;

      const provider = this.getIframeProvider(iframe);
      let message = null;

      if (provider === 'youtube') {
        const functions = {
          play: 'playVideo',
          pause: 'pauseVideo',
          mute: 'mute',
          unmute: 'unMute'
        };
        const functionName = functions[command];
        if (functionName) message = JSON.stringify({ event: 'command', func: functionName, args: [] });
      }

      if (provider === 'vimeo') {
        const methods = {
          play: { method: 'play' },
          pause: { method: 'pause' },
          mute: { method: 'setVolume', value: 0 },
          unmute: { method: 'setVolume', value: 1 }
        };
        message = methods[command] || null;
      }

      if (!message) return;

      try {
        iframe.contentWindow.postMessage(message, new URL(loadedSource).origin);
      } catch {
        iframe.contentWindow.postMessage(message, '*');
      }
    }

    startPreview({ force = false } = {}) {
      if (this.isOpen || this.hasAttribute('data-drbo-sv-dismissed')) return;
      window.clearTimeout(this.previewExpandTimer);
      window.clearTimeout(this.previewCollapseTimer);
      window.clearTimeout(this.previewReadyTimer);
      this.isPreviewMuted = true;
      this.isPreviewPlaying = false;
      this.removeAttribute('data-drbo-sv-preview-playing');
      this.removeAttribute('data-drbo-sv-preview-media-ready');
      this.setPreviewExpanded(false);
      if (this.reducedMotion.matches || this.saveData) return;
      if (!force && !this.readBoolean('data-drbo-sv-preview-auto-expand', true)) return;
      const delay = force ? 180 : Math.max(Number(this.getAttribute('data-drbo-sv-preview-expand-delay')) || 3000, 0);
      this.previewExpandTimer = window.setTimeout(() => this.startPreviewPlayback(), delay);
    }

    pausePreview({ preserveState = false } = {}) {
      window.clearTimeout(this.previewExpandTimer);
      window.clearTimeout(this.previewCollapseTimer);
      window.clearTimeout(this.previewReadyTimer);
      this.querySelectorAll(SELECTORS.previewVideo).forEach((video) => {
        this.pauseVideo(video);
      });
      this.querySelectorAll(SELECTORS.previewIframe).forEach((iframe) => {
        iframe.removeAttribute('src');
        iframe.removeAttribute('data-drbo-sv-preview-ready');
      });
      this.setPreviewExpanded(false);
      this.isPreviewPlaying = false;
      this.removeAttribute('data-drbo-sv-preview-playing');
      this.removeAttribute('data-drbo-sv-preview-media-ready');
      this.renderPreviewState();
    }

    togglePreviewPlayback() {
      if (this.isPreviewPlaying) this.pausePreview();
      else this.startPreviewPlayback();
    }

    togglePreviewMute() {
      this.isPreviewMuted = true;
    }

    renderPreviewState() {
      const expanded = this.hasAttribute('data-drbo-sv-preview-expanded');
      this.querySelectorAll(SELECTORS.previewShell).forEach((shell) => {
        shell.setAttribute('data-preview-state', expanded ? 'expanded' : 'collapsed');
      });
      this.querySelectorAll(SELECTORS.previewPlay).forEach((control) => {
        control.setAttribute('aria-pressed', String(expanded));
        control.setAttribute('aria-label', expanded ? localize("Produktvorschau einklappen", "Collapse product preview") : localize("Produktvorschau ausklappen", "Expand product preview"));
      });
    }

    setPreviewExpanded(expanded) {
      this.toggleAttribute('data-drbo-sv-preview-expanded', expanded);
      this.renderPreviewState();
    }

    startPreviewPlayback() {
      if (this.isOpen || this.hasAttribute('data-drbo-sv-dismissed')) return;

      this.expandPreviewTemporarily();
      this.isPreviewMuted = true;
      this.preparePreviewMedia();

      const videos = Array.from(this.querySelectorAll(SELECTORS.previewVideo));
      const iframes = Array.from(this.querySelectorAll(SELECTORS.previewIframe));

      videos.forEach((video) => {
        video.playsInline = true;
        Promise.resolve(this.playVideo(video)).catch(() => {});
      });

      let iframeStarted = false;
      iframes.forEach((iframe) => {
        if (!iframe.dataset.src) return;
        iframe.src = this.buildIframeSource(iframe.dataset.src, this.getIframeProvider(iframe), true);
        iframeStarted = true;
      });

      this.isPreviewPlaying = videos.length > 0 || iframeStarted;
      this.toggleAttribute('data-drbo-sv-preview-playing', this.isPreviewPlaying);
      this.renderPreviewState();
    }

    preparePreviewMedia() {
      this.querySelectorAll(SELECTORS.previewVideo).forEach((video) => {
        if (video.hasAttribute('data-drbo-sv-preview-bound')) return;
        video.setAttribute('data-drbo-sv-preview-bound', '');
        video.addEventListener('playing', () => this.markPreviewMediaReady(120), { signal: this.signal });
      });

      this.querySelectorAll(SELECTORS.previewIframe).forEach((iframe) => {
        if (iframe.hasAttribute('data-drbo-sv-preview-bound')) return;
        iframe.setAttribute('data-drbo-sv-preview-bound', '');
        iframe.addEventListener('load', () => {
          if (!iframe.getAttribute('src') || !this.isPreviewPlaying || this.isOpen) return;
          iframe.setAttribute('data-drbo-sv-preview-ready', '');
          this.markPreviewMediaReady(900);
        }, { signal: this.signal });
      });
    }

    markPreviewMediaReady(delay = 0) {
      window.clearTimeout(this.previewReadyTimer);
      this.previewReadyTimer = window.setTimeout(() => {
        if (!this.isPreviewPlaying || this.isOpen || this.hasAttribute('data-drbo-sv-dismissed')) return;
        this.setAttribute('data-drbo-sv-preview-media-ready', '');
      }, delay);
    }

    expandPreviewTemporarily() {
      if (this.isOpen || this.hasAttribute('data-drbo-sv-dismissed')) return;
      window.clearTimeout(this.previewCollapseTimer);
      this.setPreviewExpanded(true);
      const duration = Math.max(Number(this.getAttribute('data-drbo-sv-preview-expanded-duration')) || 5000, 2000);
      this.previewCollapseTimer = window.setTimeout(() => this.setPreviewExpanded(false), duration);
    }

    applyDismissedState() {
      if (window.Shopify?.designMode) {
        this.removeDismissedState();
        return;
      }

      if (!this.shouldRememberClose) return;

      try {
        if (sessionStorage.getItem(this.storageKey) === '1') {
          this.setAttribute('data-drbo-sv-dismissed', '');
        }
      } catch {
        this.removeAttribute('data-drbo-sv-dismissed');
      }
    }

    setDismissedState() {
      if (!this.shouldRememberClose) return;
      try {
        sessionStorage.setItem(this.storageKey, '1');
      } catch {
        return;
      }
    }

    removeDismissedState() {
      this.removeAttribute('data-drbo-sv-dismissed');
      try {
        sessionStorage.removeItem(this.storageKey);
      } catch {
        return;
      }
    }
  }

  customElements.define(ELEMENT_NAME, DrboShoppableVideo);
})();
